import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  PreCheckInByDayResponse,
  PreCheckOutByDayResponse,
} from "@/types/analytics-overview";
import { FlowCompletionCard } from "./FlowCompletionCard";
import { GuestPathTrackCard } from "./GuestPathTrackCard";
import {
  downloadGuestPathPreCheckInExport,
  downloadGuestPathPreCheckOutExport,
} from "./guestPathExport";
import {
  mapPreCheckInSteps,
  mapPreCheckInTrackMetrics,
  mapPreCheckOutSteps,
  mapPreCheckOutTrackMetrics,
  toCompletionRate,
  toRingValues,
} from "./guestPathMappers";

interface GuestPathSectionProps {
  preCheckIn?: PreCheckInByDayResponse;
  preCheckOut?: PreCheckOutByDayResponse;
  isLoading?: boolean;
  exportParams?: {
    hotelId: string;
    from: string;
    to: string;
    isReady: boolean;
  };
}

export const GuestPathSection: React.FC<GuestPathSectionProps> = ({
  preCheckIn,
  preCheckOut,
  isLoading = false,
  exportParams,
}) => {
  const { t } = useTranslation();
  const preCheckInSteps = mapPreCheckInSteps(preCheckIn);
  const preCheckInTrackMetrics = mapPreCheckInTrackMetrics(preCheckIn);
  const preCheckInCompleted = preCheckIn?.totals.completed ?? 0;
  const preCheckInStarted = preCheckIn?.totals.start ?? 0;
  const preCheckInCompletionRate = toCompletionRate(
    preCheckInCompleted,
    preCheckInStarted,
  );
  const preCheckInRingValues = toRingValues(preCheckInTrackMetrics);

  const preCheckOutSteps = mapPreCheckOutSteps(preCheckOut);
  const preCheckOutTrackMetrics = mapPreCheckOutTrackMetrics(preCheckOut);
  const preCheckOutCompleted = preCheckOut?.totals.completed ?? 0;
  const preCheckOutStarted = preCheckOut?.totals.start ?? 0;
  const preCheckOutCompletionRate = toCompletionRate(
    preCheckOutCompleted,
    preCheckOutStarted,
  );
  const preCheckOutRingValues = toRingValues(preCheckOutTrackMetrics);
  const isPreCheckInExportDisabled =
    !exportParams?.isReady || isLoading || !preCheckIn;
  const isPreCheckOutExportDisabled =
    !exportParams?.isReady || isLoading || !preCheckOut;

  const handleExportPreCheckIn = (format: "csv" | "xlsx") => {
    if (!exportParams?.isReady || !preCheckIn) {
      return;
    }

    const downloaded = downloadGuestPathPreCheckInExport({
      format,
      hotelId: exportParams.hotelId,
      from: exportParams.from,
      to: exportParams.to,
      data: preCheckIn,
    });

    if (!downloaded) {
      toast.error(
        t(
          "analytics.overview.toast.preCheckInExportError",
          "Failed to export pre-check-in data",
        ),
      );
      return;
    }

    toast.success(
      t(
        "analytics.overview.toast.preCheckInExportSuccess",
        "Pre-check-in export downloaded",
      ),
    );
  };

  const handleExportPreCheckOut = (format: "csv" | "xlsx") => {
    if (!exportParams?.isReady || !preCheckOut) {
      return;
    }

    const downloaded = downloadGuestPathPreCheckOutExport({
      format,
      hotelId: exportParams.hotelId,
      from: exportParams.from,
      to: exportParams.to,
      data: preCheckOut,
    });

    if (!downloaded) {
      toast.error(
        t(
          "analytics.overview.toast.preCheckOutExportError",
          "Failed to export pre-check-out data",
        ),
      );
      return;
    }

    toast.success(
      t(
        "analytics.overview.toast.preCheckOutExportSuccess",
        "Pre-check-out export downloaded",
      ),
    );
  };

  return (
    <div className={`space-y-4 ${isLoading ? "opacity-70" : ""}`}>
      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FlowCompletionCard
          title={t(
            "analytics.overview.guestPath.preCheckIn",
            "Pre-Check-In Online",
          )}
          steps={preCheckInSteps}
          onExport={handleExportPreCheckIn}
          exportDisabled={isPreCheckInExportDisabled}
        />
        <div className="min-w-0">
          <GuestPathTrackCard
            metrics={preCheckInTrackMetrics}
            ringValues={preCheckInRingValues}
            completionRate={preCheckInCompletionRate}
            totalCompleted={preCheckInCompleted}
            totalStarted={preCheckInStarted}
            onExport={handleExportPreCheckIn}
            exportDisabled={isPreCheckInExportDisabled}
          />
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FlowCompletionCard
          title={t(
            "analytics.overview.guestPath.preCheckOut",
            "Pre-Check-Out Online",
          )}
          steps={preCheckOutSteps}
          onExport={handleExportPreCheckOut}
          exportDisabled={isPreCheckOutExportDisabled}
        />
        <div className="min-w-0">
          <GuestPathTrackCard
            metrics={preCheckOutTrackMetrics}
            ringValues={preCheckOutRingValues}
            completionRate={preCheckOutCompletionRate}
            totalCompleted={preCheckOutCompleted}
            totalStarted={preCheckOutStarted}
            onExport={handleExportPreCheckOut}
            exportDisabled={isPreCheckOutExportDisabled}
          />
        </div>
      </div>
    </div>
  );
};
